import { Block, Chips } from "@business-finland/wif-ui-lib";
import CompanyListUI from "@business-finland/wif-ui-lib/dist/CompanyList";
import { Pagination } from "@business-finland/wif-ui-lib/dist/Pagination";
import { useCallback, useState, useRef } from "react";

import useViewport from "../../../contexts/ViewportContext";
import type { WifCompany, WifService } from "../../../types/wif-companies";
import useCompaniesQuery from "./useCompaniesQuery";
import styles from "./CompanyList.module.scss";

export interface ICompanyListProps {
  title?: string;
  categoryTitle?: string;
  services?: WifService[];
  initCompanyResults: Wif.Mgnl.Results<WifCompany> | null;
}

const companyListId = "company_list";
const smallPadding = Block.Padding.SMALL;
const noPadding = Block.Padding.NONE;

export default function CompanyList({
  title = "Service Partners",
  categoryTitle = "Type of services",
  services = [],
  initCompanyResults,
}: ICompanyListProps): JSX.Element {
  const ref = useRef<HTMLDivElement>(null);

  const limit = useLimit();
  const [page, setPage] = useState(1);
  const [selectedService, setSelectedService] = useState<string | undefined>(
    undefined
  );
  const { pageCount, totalCount, companyCards } = useCompaniesQuery(
    selectedService,
    page,
    limit,
    initCompanyResults ?? undefined
  );

  const toggleSelectedService = useCallback((serviceValue: string) => {
    setSelectedService((current) =>
      current === serviceValue ? undefined : serviceValue
    );
  }, []);

  const handlePageChange = useCallback((page: number) => {
    setPage(page);
    if (typeof window !== "undefined") {
      const top = ref.current?.offsetTop || 0;
      window.scrollTo(0, top - 80);
    }
  }, []);

  return (
    <div id={companyListId} className={getStyleClassName()} ref={ref}>
      <CompanyCategories
        title={categoryTitle}
        services={services}
        selectedService={selectedService}
        toggleSelectedService={toggleSelectedService}
      />

      <Block topPadding={smallPadding} bottomPadding={noPadding}>
        <Block.Content flex>
          <h2 className={getStyleClassName("header") + " heading__section"}>
            {totalCount} {title}
          </h2>
        </Block.Content>
      </Block>

      <CompanyListUI
        topPadding={smallPadding}
        bottomPadding={smallPadding}
        skeletonLoadingCount={limit}
        companyCards={companyCards}
      />

      {pageCount > 1 ? (
        <Pagination
          count={pageCount}
          value={page}
          onChange={handlePageChange}
          i18n={{
            btnNextText: "Next",
            btnPageText: "Page",
            btnPrevText: "Previous",
          }}
        />
      ) : null}
    </div>
  );
}

function useLimit(): number {
  const { viewport } = useViewport();

  if (viewport === "xs" || viewport === "sm") return 4;
  if (viewport === "md" || viewport === "lg") return 6;
  return 9;
}

interface ICompanyCategoriesProps {
  title: string;
  services: WifService[];
  selectedService?: string;
  toggleSelectedService: (serviceValue: string) => void;
}

function CompanyCategories({
  services,
  title,
  toggleSelectedService,
  selectedService,
}: ICompanyCategoriesProps): JSX.Element | null {
  if (!Array.isArray(services) || services.length === 0) {
    return null;
  }

  return (
    <Block as="header" topPadding={smallPadding} bottomPadding={noPadding}>
      <Block.Content flex className={getStyleClassName("categories")}>
        <p className={"texts__component_title--bold"}>{title}</p>

        <div className={getStyleClassName("categories", "chips")}>
          {services.map((service) => (
            <Chips
              key={service.value}
              as="button"
              text={service.label?.toString() || service.value}
              onClick={() => toggleSelectedService(service.value)}
              selected={selectedService === service.value}
              clipLongText={false}
            />
          ))}
        </div>
      </Block.Content>
    </Block>
  );
}

export function getStyleClassName(...suffixes: string[]) {
  return styles[[companyListId, ...suffixes].join("__")];
}
